/**
 * @fileoverview Internationalization (i18n) configuration.
 * @summary Central i18n entry point assembling locale files.
 * @package PlayItNow
 * @module apps/front/lib/i18n
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { fr } from './locales/fr'
import { en } from './locales/en'
import { es } from './locales/es'
import { pt } from './locales/pt'
import { de } from './locales/de'
import { it } from './locales/it'

export type Language = 'fr' | 'en' | 'es' | 'pt' | 'de' | 'it'

export interface Translations {
  [key: string]: string | Translations
}

const translations: Record<Language, Translations> = {
  fr,
  en,
  es,
  pt,
  de,
  it,
}

/**
 * @summary Try to get translation for a language.
 */
function tryGet(lang: Language, keys: string[]): string | null {
  let value: unknown = translations[lang]
  for (const k of keys) {
    if (value && typeof value === 'object' && k in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[k]
    } else {
      return null
    }
  }
  return typeof value === 'string' ? value : null
}

/**
 * @summary Get translation for a key path.
 * @param lang Language code.
 * @param key Translation key path (e.g., 'login.title').
 * @param fallback Fallback value if translation not found.
 * @returns Translated string.
 */
export function t(lang: Language, key: string, fallback?: string): string {
  const keys = key.split('.')
  const result = tryGet(lang, keys)
  if (result !== null) return result
  if (lang !== 'en') {
    const enResult = tryGet('en', keys)
    if (enResult !== null) return enResult
  }
  return fallback || key
}

const SUPPORTED_LANGS: readonly Language[] = ['fr', 'en', 'es', 'pt', 'de', 'it']

/**
 * @summary Map browser locale (e.g. 'fr-FR', 'en-US') to a supported language.
 */
function getBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'fr'
  const preferred = navigator.language || (navigator.languages && navigator.languages[0])
  if (preferred) {
    const code = preferred.slice(0, 2).toLowerCase() as Language
    if (SUPPORTED_LANGS.includes(code)) return code
  }
  if (navigator.languages) {
    for (const locale of navigator.languages) {
      const code = locale.slice(0, 2).toLowerCase() as Language
      if (SUPPORTED_LANGS.includes(code)) return code
    }
  }
  return 'fr'
}

/**
 * @summary Get current language: localStorage, then browser preference, then default (fr).
 */
export function getCurrentLanguage(): Language {
  if (typeof window === 'undefined') return 'fr'
  const stored = localStorage.getItem('playitnow_lang') as Language | null
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored
  return getBrowserLanguage()
}

/**
 * @summary Set current language.
 */
export function setCurrentLanguage(lang: Language): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('playitnow_lang', lang)
  }
}
